import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Pages
import HomePage from '@/pages/HomePage';
import MarketplacePage from '@/pages/MarketplacePage';
import ListingDetailPage from '@/pages/ListingDetailPage';
import EncyclopediaPage from '@/pages/EncyclopediaPage';
import BreedDetailPage from '@/pages/BreedDetailPage';
import DogMatcherPage from '@/pages/DogMatcherPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostPage from '@/pages/BlogPostPage';
import DashboardPage from '@/pages/DashboardPage';
import CreateListingPage from '@/pages/CreateListingPage';
import AuthPage from '@/pages/AuthPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import FAQPage from '@/pages/FAQPage';
import FavoritesPage from '@/pages/FavoritesPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
}

function AppRouter() {
  return (
    <>
      <Navbar />
      <main className="min-h-[calc(100vh-4rem)]">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/dogs-for-sale" element={<MarketplacePage />} />
          <Route path="/dogs-for-sale/:id" element={<ListingDetailPage />} />
          <Route path="/dog-breeds" element={<EncyclopediaPage />} />
          <Route path="/dog-breeds/:slug" element={<BreedDetailPage />} />
          <Route path="/dog-matcher" element={<DogMatcherPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/callback" element={<Navigate to="/auth" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard/new" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
          <Route path="/dashboard/edit/:id" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
          <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function getRouterBasename() {
  if (typeof window === 'undefined') return undefined;

  const path = window.location.pathname || '/';
  const lowerPath = path.toLowerCase();
  const hostedBasePaths = ['/doggohub', '/frontend'];

  for (const basePath of hostedBasePaths) {
    const baseIndex = lowerPath.indexOf(basePath);
    if (baseIndex >= 0) {
      return path.slice(0, baseIndex + basePath.length);
    }
  }

  const publicUrl = process.env.PUBLIC_URL;
  if (publicUrl && publicUrl !== '.') return publicUrl;
  return undefined;
}

function App() {
  const useHashRouter = typeof window !== 'undefined' && window.location.protocol === 'file:';
  const Router = useHashRouter ? HashRouter : BrowserRouter;
  const routerProps = useHashRouter ? {} : { basename: getRouterBasename() };

  return (
    <Router {...routerProps}>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </Router>
  );
}

export default App;
